/**
 * 轻提示（Toast）。
 *
 * 背景：保存成功、复制成功、接口失败这类结果需要一个不打断操作的反馈。
 * 设计意图：Provider 挂在应用根部；error() 直接接收接口异常，按错误码翻成当前语言（common.errorText），
 *   页面里不用每处都写一遍映射。成功提示 3 秒、错误提示 6 秒后消失。
 * 关键约束：层级 10000，高于弹窗。
 */
import { IconAlertCircle, IconCheck } from '@tabler/icons-react'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'

interface ToastItem {
  id: number
  kind: 'ok' | 'error'
  text: string
}

interface ToastApi {
  ok: (text: string) => void
  error: (errOrText: unknown) => void
}

const Ctx = createContext<ToastApi | null>(null)
let seq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n()
  const [items, setItems] = useState<ToastItem[]>([])

  const push = useCallback((kind: ToastItem['kind'], text: string) => {
    const id = ++seq
    setItems((xs) => [...xs.slice(-3), { id, kind, text }])
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === 'ok' ? 3000 : 6000)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      ok: (text) => push('ok', text),
      error: (e) => {
        if (typeof e === 'string') push('error', e)
        else {
          console.error('[ui] 操作失败', e)
          push('error', errorText(e, locale))
        }
      },
    }),
    [push, locale],
  )

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div className="ap-toasts" aria-live="polite">
          {items.map((it) => (
            <div key={it.id} className={`ap-toast ${it.kind === 'error' ? 'ap-toast--error' : ''}`}>
              {it.kind === 'ok' ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
              {it.text}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
